/* eslint-disable react/no-danger */
import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatMoney, formatPercent } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import moment from 'moment-timezone';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { betreuerByNrSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { FilterValues, LineData } from './types';
import { RechnungSummaryProps, useRechnungSummary } from './hooks/useRechnungSummary';
import { getBetreuerAnteil } from '../auftragsuebersicht/utils';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    growth: {
        fontSize: 12,
        lineHeight: 12,
    },
    positiveGrowth: {
        color: palette.green,
    },
    negativeGrowth: {
        color: palette.red,
    },
    auNr: {},
    ekNetto: {
        backgroundColor: odd,
    },
    vkNetto: {
        backgroundColor: even,
    },
    bMarge: {
        backgroundColor: odd,
    },
    tippProv: {
        backgroundColor: even,
    },
    skonto: {
        backgroundColor: odd,
    },
    fracht: {
        backgroundColor: even,
    },
    nMarge: {
        backgroundColor: odd,
    },
    bAnteil: {
        backgroundColor: even,
    },
    fAnteil: {
        backgroundColor: odd,
    },
    fAnteilTotal: {
        backgroundColor: odd,
        fontWeight: 700,
    },
    borderRight: {
        borderRight: '1px solid',
    },
});

interface BetreuerabrechnungListProps extends FilterValues {
    printView?: boolean;
}

export interface BetreuerabrechnungHandle {
    finish: () => Promise<string>;
}

export const Betreuerabrechnung = React.forwardRef(
    ({ abrJahr, abrMo, currentMonth, betreuer, showFAnteil, printView = false }: BetreuerabrechnungListProps, ref) => {
        const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
        const betreuerByNr = useSelector(betreuerByNrSelector);
        const betreuerprovisionListByNr = useBetreuerprovisionListByNr();

        const rechnungSummaryProps = React.useMemo<RechnungSummaryProps>(
            () => ({
                abrJahr,
                abrMo,
                currentMonth,
                betreuer,
                showFAnteil,
                enabled: true,
            }),
            [abrJahr, abrMo, currentMonth, betreuer, showFAnteil],
        );

        const rechnungSummary = useRechnungSummary(rechnungSummaryProps);

        React.useImperativeHandle(ref, () => ({
            finish: rechnungSummary.finish,
        }));

        const betreuerAnteil = getBetreuerAnteil(
            betreuerprovisionListByNr,
            betreuer || 0,
            abrJahr + '-' + abrMo + '-01',
            rechnungSummary.total.nMarge,
            0,
        );

        const renderHeaderRow = () => {
            const moneyClassName = classnames(
                styles.tdHead,
                formStyles.dataValueRight,
                printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
            );

            return (
                <tr>
                    <td
                        className={classnames(
                            styles.tdHead,
                            printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                        )}
                    >
                        RechnungNr.
                    </td>
                    <td
                        className={classnames(
                            styles.tdHead,
                            printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                        )}
                    >
                        AuftragNr.
                    </td>
                    <td
                        className={classnames(
                            styles.tdHead,
                            printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                        )}
                    >
                        R-Datum
                    </td>
                    <td
                        className={classnames(
                            styles.tdHead,
                            printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                        )}
                    >
                        Bez.Dat.
                    </td>
                    <td
                        className={classnames(
                            styles.tdHead,
                            printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                        )}
                    >
                        Kunde
                    </td>
                    <td className={classnames(moneyClassName, styles.ekNetto)}>EK Netto</td>
                    <td className={classnames(moneyClassName, styles.vkNetto)}>VK Netto</td>
                    <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>B-Marge</td>
                    <td className={classnames(moneyClassName, styles.tippProv)}>Tipp-Pr.</td>
                    <td className={classnames(moneyClassName, styles.skonto)}>Skonto</td>
                    <td className={classnames(moneyClassName, styles.fracht)}>Fracht</td>
                    <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>N-Marge</td>
                    <td className={classnames(moneyClassName, styles.bAnteil)}>B-Anteil</td>
                    {showFAnteil && <td className={classnames(moneyClassName, styles.fAnteil)}>F-Anteil</td>}
                </tr>
            );
        };

        const moneyClassName = classnames(
            styles.tdBody,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );
        const percentClassName = classnames(
            styles.tdBody,
            formStyles.dataValueRight,
            printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
        );

        const renderMoney = (value: number, className: string, borderRight?: boolean) => {
            return (
                <td
                    className={classnames(moneyClassName, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {formatMoney(value)}
                </td>
            );
        };

        const renderSummaryLine = (title: string, line: LineData) => {
            return (
                <tr key={title}>
                    <td colSpan={5}>{title}</td>
                    {renderMoney(line.ekNetto, styles.ekNetto)}
                    {renderMoney(line.vkNetto, styles.vkNetto)}
                    {renderMoney(line.bMarge, styles.bMarge, true)}
                    {renderMoney(line.tippProv, styles.tippProv)}
                    {renderMoney(line.skonto, styles.skonto)}
                    {renderMoney(line.fracht, styles.fracht)}
                    {renderMoney(line.nMarge, styles.nMarge, true)}
                    {renderMoney(line.bAnteil, styles.bAnteil)}
                    {showFAnteil && renderMoney(line.fAnteil, styles.fAnteil)}
                </tr>
            );
        };

        const renderLine = (line: LineData) => {
            const rechnungUrl = createRecordUrl('rechnungen', line.kNr, line.rNr);
            const auftragUrl = createRecordUrl('auftraege', line.kNr, line.auNr);

            return (
                <tr key={line.rNr}>
                    <td
                        className={classnames(
                            styles.tdBody,
                            printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                        )}
                    >
                        {printView ? line.rNr : <OpenLink link={rechnungUrl} label={line.rNr} />}
                    </td>
                    <td
                        className={classnames(
                            styles.tdBody,
                            printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                        )}
                    >
                        {printView ? line.auNr : <OpenLink link={auftragUrl} label={line.auNr} />}
                    </td>
                    <td
                        className={classnames(
                            styles.tdBody,
                            printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                        )}
                    >
                        {moment(line.datum).format('DD.MM.YYYY')}
                    </td>
                    <td
                        className={classnames(
                            styles.tdBody,
                            printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                        )}
                    >
                        {moment(line.bezDat).format('DD.MM.YYYY')}
                    </td>
                    <td
                        className={classnames(
                            styles.tdBody,
                            printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                        )}
                    >
                        {line.kName}
                    </td>
                    {renderMoney(line.ekNetto, styles.ekNetto)}
                    {renderMoney(line.vkNetto, styles.vkNetto)}
                    {renderMoney(line.bMarge, styles.bMarge, true)}
                    {renderMoney(line.tippProv, styles.tippProv)}
                    {renderMoney(line.skonto, styles.skonto)}
                    {renderMoney(line.fracht, styles.fracht)}
                    {renderMoney(line.nMarge, styles.nMarge, true)}
                    {renderMoney(line.bAnteil, styles.bAnteil)}
                    {showFAnteil && renderMoney(line.fAnteil, styles.fAnteil)}
                </tr>
            );
        };

        if (!lookupsLoadingFinished) {
            return <div />;
        }

        return (
            <div className={printView ? styles.printContainer : styles.container}>
                {printView && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                // eslint-disable-next-line max-len
                                '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                        }}
                    />
                )}
                {!printView && <table className={formStyles.reportFixedHeaderNoBgColor}>{renderHeaderRow()}</table>}
                <h1>
                    Betreuerabrechnung - {betreuerByNr[betreuer || 0]?.name || betreuerByNr} - {abrJahr}-
                    {abrMo.toString().padStart(2, '0')}
                </h1>
                {printView && (
                    <table className={formStyles.reportFilterInfo}>
                        <tr>
                            <td>Druckdatum: </td>
                            <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                        </tr>
                    </table>
                )}
                <table className={formStyles.reportTable}>
                    <thead>{renderHeaderRow()}</thead>
                    <tbody>
                        {(rechnungSummary.rechnungLineDataList || []).map((line) => renderLine(line))}

                        <tr>
                            <td colSpan={16} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                        </tr>

                        {renderSummaryLine(
                            'Gesamt (' + (rechnungSummary ? rechnungSummary.count : '...') + ')',
                            rechnungSummary.total,
                        )}
                        <tr>
                            <td colSpan={5}>%-Anteil</td>
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />

                            <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>
                                {formatPercent(rechnungSummary.total.bMarge / (rechnungSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(moneyClassName, styles.tippProv)}>
                                {formatPercent(
                                    rechnungSummary.total.tippProv / (rechnungSummary.total.vkNetto / 100),
                                    2,
                                )}
                            </td>
                            <td className={classnames(moneyClassName, styles.skonto)}>
                                {formatPercent(rechnungSummary.total.skonto / (rechnungSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(moneyClassName, styles.fracht)}>
                                {formatPercent(rechnungSummary.total.fracht / (rechnungSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>
                                {formatPercent(rechnungSummary.total.nMarge / (rechnungSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(moneyClassName, styles.bAnteil)}>
                                {formatPercent(
                                    rechnungSummary.total.bAnteil / (rechnungSummary.total.vkNetto / 100),
                                    2,
                                )}
                            </td>
                            {showFAnteil && (
                                <td className={classnames(moneyClassName, styles.fAnteil)}>
                                    {formatPercent(
                                        rechnungSummary.total.fAnteil / (rechnungSummary.total.vkNetto / 100),
                                        2,
                                    )}
                                </td>
                            )}
                        </tr>
                        <tr>
                            <td colSpan={5}>Betreuer ({rechnungSummary.total.betrUmCount})</td>
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            {renderMoney(rechnungSummary.total.betrUm, styles.vkNetto)}
                            <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)}>
                                {rechnungSummary.total.vkNetto
                                    ? formatPercent(
                                          rechnungSummary.total.betrUm / (rechnungSummary.total.vkNetto / 100),
                                          2,
                                      )
                                    : ''}
                            </td>
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            {showFAnteil && <td className={classnames(percentClassName, styles.fAnteil)} />}
                        </tr>

                        <tr>
                            <td colSpan={16} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                        </tr>

                        {Object.values(rechnungSummary.betreuerTotal)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((betreuerTotal) => {
                                return renderSummaryLine(betreuerTotal.name || '?', betreuerTotal);
                            })}
                        <tr>
                            <td colSpan={16} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                        </tr>

                        <tr>
                            <td colSpan={5}>Gesamt </td>
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />
                            <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            {showFAnteil && renderMoney(rechnungSummary.fAnteilTotal, styles.fAnteilTotal)}
                        </tr>
                        <tr>
                            <td colSpan={15} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                        </tr>
                        <tr>
                            <td colSpan={5}>&nbsp;</td>
                        </tr>
                        <tr>
                            <td colSpan={7}>&nbsp;</td>
                            <td colSpan={3}>Provisionsaufteilung</td>
                        </tr>
                        {(betreuerAnteil.parts || []).map((part, i) => (
                            <tr key={part.limit}>
                                <td colSpan={10}>&nbsp;</td>
                                <td className={formStyles.reportCellRight}>{formatMoney(part.limit)}</td>
                                <td className={formStyles.reportCellRight}>{formatPercent(part.provision)}</td>
                                <td className={formStyles.reportCellRight}>
                                    {formatMoney(
                                        i === 0 ? part.anteil : part.anteil - betreuerAnteil.parts[i - 1].anteil,
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={7}>&nbsp;</td>
                            <td colSpan={3}>Provisionssumme:</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td className={formStyles.reportCellRight}>
                                <strong>
                                    {formatMoney(betreuerAnteil.parts[betreuerAnteil.parts.length - 1]?.anteil || 0)}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    },
);
